import { useEffect, useState } from '../../vendor/react';
import { authProvider } from './provider';
import type { ActiveContext, AuthResult, AuthSession, OnboardingPayload, Workspace } from './types';
import { ensureOrganizationSubscription, hasFeature, setActiveEdition } from '../saas/saas-store';

type AuthStatus = 'idle' | 'loading' | 'ready';

type AuthState = {
  status: AuthStatus;
  providerMode: 'local' | 'supabase';
  session: AuthSession | null;
  workspace: Workspace | null;
  error: string;
};

const initialState: AuthState = {
  status: 'idle',
  providerMode: authProvider.mode,
  session: null,
  workspace: null,
  error: '',
};

let state = initialState;
let initialization: Promise<void> | null = null;
let sessionRefresh: Promise<AuthSession | null> | null = null;
const listeners = new Set<() => void>();

const emit = () => listeners.forEach((listener) => listener());
const update = (patch: Partial<AuthState>) => {
  state = { ...state, ...patch };
  emit();
};

const setSession = async (session: AuthSession | null) => {
  if (!session) {
    update({ session: null, workspace: null, status: 'ready', error: '' });
    return;
  }
  const workspace = await authProvider.loadWorkspace(session);
  if (workspace.activeContext?.organizationId) { ensureOrganizationSubscription(workspace.activeContext.organizationId); setActiveEdition(workspace.activeContext.organizationId); }
  update({ session, workspace, status: 'ready', error: '' });
};

export const initializeAuth = () => {
  if (initialization) return initialization;
  initialization = (async () => {
    update({ status: 'loading', error: '' });
    try {
      const session = await authProvider.restoreSession();
      await setSession(session);
    } catch (error) {
      update({ status: 'ready', session: null, workspace: null, error: error instanceof Error ? error.message : 'No se pudo restaurar la sesión.' });
    }
  })();
  return initialization;
};

export const getValidAuthSession = async (options: { force?: boolean; rejectedAccessToken?: string } = {}): Promise<AuthSession | null> => {
  const current = state.session;
  if (!current) return null;
  if (current.provider !== 'supabase') return current;
  if (options.rejectedAccessToken && current.accessToken !== options.rejectedAccessToken) return current;
  if (!options.force && current.expiresAt > Date.now() + 2 * 60_000) return current;
  if (!authProvider.refreshSession) return current;

  if (!sessionRefresh) {
    sessionRefresh = authProvider.refreshSession(current)
      .then((session) => {
        update({ session, error: '' });
        return session;
      })
      .catch(() => {
        const message = 'Tu sesión venció. Inicia sesión nuevamente.';
        update({ session: null, workspace: null, status: 'ready', error: message });
        throw new Error(message);
      })
      .finally(() => { sessionRefresh = null; });
  }
  return sessionRefresh;
};

export const authActions = {
  clearError() {
    update({ error: '' });
  },

  async signIn(email: string, password: string): Promise<AuthResult> {
    update({ status: 'loading', error: '' });
    try {
      const result = await authProvider.signIn(email, password);
      if (result.session) await setSession(result.session);
      else update({ status: 'ready' });
      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'No se pudo iniciar sesión.';
      update({ status: 'ready', error: message });
      throw new Error(message);
    }
  },

  async signUp(input: { email: string; password: string; firstName: string; lastName: string }): Promise<AuthResult> {
    update({ status: 'loading', error: '' });
    try {
      const result = await authProvider.signUp(input);
      if (result.session) await setSession(result.session);
      else update({ status: 'ready' });
      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'No se pudo crear la cuenta.';
      update({ status: 'ready', error: message });
      throw new Error(message);
    }
  },

  async signOut() {
    const session = state.session;
    if (session) await authProvider.signOut(session);
    update({ session: null, workspace: null, status: 'ready', error: '' });
  },

  async requestMagicLink(email: string) {
    update({ error: '' });
    return authProvider.requestMagicLink(email);
  },

  async requestPasswordReset(email: string) {
    update({ error: '' });
    return authProvider.requestPasswordReset(email);
  },

  async confirmEmail(token: string) {
    update({ status: 'loading', error: '' });
    try {
      const session = await authProvider.confirmEmail(token);
      await setSession(session);
      return session;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'No se pudo confirmar el correo.';
      update({ status: 'ready', error: message });
      throw new Error(message);
    }
  },

  async consumeMagicLink(token: string) {
    update({ status: 'loading', error: '' });
    try {
      const session = await authProvider.consumeMagicLink(token);
      await setSession(session);
      return session;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'No se pudo abrir el enlace.';
      update({ status: 'ready', error: message });
      throw new Error(message);
    }
  },

  async consumeCallback(params: Record<string, string>) {
    if (!authProvider.consumeCallback) throw new Error('Este proveedor no utiliza callback externo.');
    update({ status: 'loading', error: '' });
    try {
      const session = await authProvider.consumeCallback(params);
      await setSession(session);
      return session;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'No se pudo completar el acceso.';
      update({ status: 'ready', error: message });
      throw new Error(message);
    }
  },

  async resetPassword(token: string, password: string) {
    update({ status: 'loading', error: '' });
    try {
      await authProvider.resetPassword(token, password);
      update({ status: 'ready' });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'No se pudo cambiar la contraseña.';
      update({ status: 'ready', error: message });
      throw new Error(message);
    }
  },

  async completeOnboarding(payload: OnboardingPayload) {
    if (!state.session) throw new Error('Necesitas una sesión activa.');
    update({ status: 'loading', error: '' });
    try {
      const workspace = await authProvider.completeOnboarding(state.session, payload);
      const session = {
        ...state.session,
        user: { ...state.session.user, firstName: payload.owner.firstName, lastName: payload.owner.lastName },
      };
      if (workspace.activeContext?.organizationId) { ensureOrganizationSubscription(workspace.activeContext.organizationId); setActiveEdition(workspace.activeContext.organizationId); }
      update({ workspace, session, status: 'ready' });
      return workspace;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'No se pudo terminar la configuración.';
      update({ status: 'ready', error: message });
      throw new Error(message);
    }
  },

  async switchContext(context: ActiveContext) {
    if (!state.session) throw new Error('Necesitas una sesión activa.');
    update({ status: 'loading', error: '' });
    try {
      const current = state.workspace;
      const organizationBusinesses = current?.businesses.filter((item) => item.organizationId === context.organizationId) ?? [];
      const firstBusiness = organizationBusinesses[0];
      const firstBranch = current?.branches.find((item) => item.businessId === (firstBusiness?.id ?? context.businessId));
      if (!hasFeature(context.organizationId, 'multiBusiness') && firstBusiness && context.businessId !== firstBusiness.id) throw new Error('Tu plan permite un solo negocio.');
      if (!hasFeature(context.organizationId, 'multiBranch') && firstBranch && context.branchId !== firstBranch.id) throw new Error('Tu plan permite un solo punto de venta.');
      const workspace = await authProvider.switchContext(state.session, context);
      ensureOrganizationSubscription(context.organizationId); setActiveEdition(context.organizationId);
      update({ workspace, status: 'ready' });
      return workspace;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'No se pudo cambiar de contexto.';
      update({ status: 'ready', error: message });
      throw new Error(message);
    }
  },
};

export const useAuth = () => {
  const [, setVersion] = useState(0);
  useEffect(() => {
    const listener = () => setVersion((value) => value + 1);
    listeners.add(listener);
    return () => listeners.delete(listener);
  }, []);
  return { ...state, actions: authActions };
};

export const getAuthState = () => state;
